import classNames from 'classnames';
import inflection from 'inflection';
import React from 'react';
import EntityRow from '../components/EntityRow';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import pinIcon from '../icons/prefilled/pinOutlineWithStroke.svg';
import { usePostSeries } from '../series/useAllSeries';
import { Link } from '../utils/router/v3Bridge';
import postPath from '../utils/url/postPath';
import PostDetailPreview from './PostDetailPreview';
import PostIcon from './PostIcon';
import styles from './PostRow.module.scss';
import PostTitleWithIcons from './PostTitleWithIcons';
import PostSeriesMeta from './series/PostSeriesMeta';

const POST_ROW_POST_FRAGMENT = gql(`
  fragment PostRow_post on SlimPostFields {
    id
    publishedAt
    userViewedAt
    updatedAt
    views
    type
    ... on Post {
      insertedAt
      series {
        id
        ...PostSeriesMeta_series
      }
    }
    ...usePostSeries_post
    contributors {
      id
    }
    ...PostIcon_post
    ...postPath_post
    ...PostTitleWithIcons_post
  }
`);

type PostFragment = DocumentType<typeof POST_ROW_POST_FRAGMENT>;

export type MetadataAttribute =
  | 'popularity'
  | 'publishedAt'
  | 'updatedAt'
  | 'userViewedAt'
  | 'insertedAt';

const getMetadata = (
  post: PostFragment,
  attribute: MetadataAttribute | null,
) => {
  switch (attribute) {
    case 'popularity':
      return `${post.views} ${inflection.inflect('view', post.views)}`;
    case 'publishedAt':
      return post.publishedAt && <Timestamp date={post.publishedAt} />;
    case 'userViewedAt':
      return post.userViewedAt && <Timestamp date={post.userViewedAt} />;
    case 'insertedAt':
      return (
        'insertedAt' in post &&
        post.insertedAt && <Timestamp date={post.insertedAt} />
      );
    default:
      return <Timestamp date={post.updatedAt} />;
  }
};

interface PostRowProps {
  pinned?: boolean;
  post: PostFragment;
  inList?: boolean;
  seriesHoverCardEnabled?: boolean;
  metadataAttribute?: MetadataAttribute | null;
  activePreviewId?: string | null;
  onActivePreviewIdChange?: ((id: string | null) => void) | null;
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
}

const PostRow = ({
  renderOverflowMenu,
  pinned = false,
  post,
  inList = false,
  seriesHoverCardEnabled = false,
  metadataAttribute = null,
  activePreviewId = null,
  onActivePreviewIdChange = null,
}: PostRowProps) => {
  const path = postPath(post);

  const postSeries = usePostSeries(post);

  const preview = (isOpen: boolean) => (
    <PostDetailPreview postId={post.id} open={isOpen} />
  );

  return (
    <EntityRow
      inList={inList}
      meta={getMetadata(post, metadataAttribute)}
      contributorIds={post.contributors.map((contributor) => contributor.id)}
      path={path}
      preview={
        onActivePreviewIdChange
          ? {
              node: preview,
              activePreviewId,
              onActivePreviewIdChange,
              id: post.id,
            }
          : undefined
      }
      icon={
        pinned ? (
          <>
            <PostIcon post={post} />
            <div
              className={classNames(styles.pinContainer, {
                [styles.post]: post.type === PostType.Post,
                [styles.template]: post.type === PostType.Template,
              })}
            >
              <SVGIcon size="small">{pinIcon}</SVGIcon>
            </div>
          </>
        ) : (
          <PostIcon post={post} />
        )
      }
      renderOverflowMenu={renderOverflowMenu}
      aboveTitle={
        postSeries ? (
          <Link className={styles.seriesHoverTargetContainer} to={path}>
            <div className={styles.seriesHoverTarget}>
              <PostSeriesMeta
                postId={post.id}
                series={postSeries}
                showHoverCard={seriesHoverCardEnabled}
                longHoverDelay={seriesHoverCardEnabled}
              />
            </div>
          </Link>
        ) : null
      }
      title={<PostTitleWithIcons post={post} />}
      aria-label={`Post: ${post.title}`}
    />
  );
};

export default PostRow;
